import getCm from '../../net/ConnectionManager';
import type { IConnectionManager, IWebSocketAbortable } from '../../net/ConnectionManager';
import type { ActionCreatorWithPreparedPayload, ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import { PubSub, PubSubEventTypes, NeuroEventTypes } from 'neuro-event-commons';
import type { RootState, TDispatch } from '../index';
import { ActionableErrorContext, Loggable, makeLog, UsageAnalyticDatapoint } from 'Utility/logger';
import { checkJWTNotExpired, parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { LOAD, SUBSCRIBERS_CHANGED, WEBSOCKET_CLOSED, sessionInvalidatedAction } from './index';
import { actions, reduxActions } from 'Store/documents/actions';
import { isAttachmentPath } from 'Utility/location';

export interface IWebSocketManager {
  /**
   * The main starter of the instance. Claim the session ownership and establish
   * a real-time connection with the backend.
   */
  connect(base64_encoded_jwt: string): Promise<void>;

  /**
   * Close the sockets and channels and whatnot and pass them to garbage
   * collecting.
   */
  disconnect(): void;

  /**
   * For example, post something that invalidates older concurrent sessions (in
   * peer browsing contexts of the same browser).
   */
  post_session_orchestration_message(message: any): void;
}

type WebSocketConnectionPolicy = {
  /**
   * The maximum number of times a WebSocket connection shall be attempted to be
   * established.
   */
  max_connection_attempts_count: number;

  /**
   * Maximum time after which a single WebSocket connection attempt is aborted,
   * in milliseconds.
   *
   * _Maximum_ because other mechanisms (such as the underlying web browser
   * platform) may abort the attempt even sooner.
   *
   * Does not account for any event loop queuing time, though!
   */
  connection_attempt_timeout_ms: number;

  /**
   * Minimum delay between two consecutive attempts to establish a WebSocket
   * connection, in milliseconds.
   *
   * _Minimum_ because of the event loop -- there's often a bunch of other work
   * being done "at the same time" in bulky frontend apps!
   */
  connection_reattempt_delay_ms: number;
};

type Hooks = {
  /**
   * Redux state reader.
   */
  get_state: () => RootState;

  /**
   * Redux state update dispatcher.
   */
  dispatch: TDispatch;

  /**
   * Redux state action creator for when a subscription message is received.
   */
  action_creator_get_subscription_message: ActionCreatorWithPreparedPayload<
    [message: PubSub.ISubscriptionSummaryMessage],
    PubSub.ISubscriptionSummaryMessage,
    typeof SUBSCRIBERS_CHANGED
  >;

  /**
   * Redux state action creator for when WebSocket gets closed.
   */
  action_creator_websocket_closed: ActionCreatorWithoutPayload<typeof WEBSOCKET_CLOSED>;
};

enum CloseReason {
  /** JWT session expired. */
  UserSessionExpired = 4000,
  /** User opened another session while still having an old one. */
  UserSessionDuplicated = 4001,
  /** Server is terminating. */
  ServerTerminating = 4002,
}

/**
 * Maintains WebSocket connection, concurrent sessions orchestrating
 * BroadcastChannel and their associated event listeners and hooks them into
 * Redux state.
 *
 * If you wish to change the implementation, here are some requirements that
 * should be taken into consideration and rely solely on manual testing due to
 * their distributed system nature:
 *
 *  1) Session invalidation by duplication for sessions in peer browsing
 *     contexts (i.e. e.g. tabs of the same browser on the same machine).
 *        If a user duplicates the tab, the older tab should become invalid and
 *     only the new tab should remain for use. Implemented client-side using
 *     Broadcast Channel API.
 *        An invalidated session should not attempt to reconnect WebSocket.
 *
 *  2) Session invalidation by duplication for sessions on different browsers or
 *     machines. Similar to the above case #1, but here Broadcast Channel API is
 *     of no use. Requires session orchestration on server-side.
 *        An invalidated session should not attempt to reconnect WebSocket.
 *
 *  3) Reconnecting WebSocket after graceful server restart. If the WebSocket
 *     backend (Neuro API) is restarted, it closes sockets gracefully and any
 *     clients should try to reconnect to the restarted backend ASAP.
 *
 *  4) Reconnecting WebSocket after some not-graceful catastrophic event. Say
 *     e.g. some network device explodes, the socket gets closed by some chain
 *     of events and the WebSocket connection gets terminated not-cleanly (as
 *     per the WebSocket protocol). In that case the client should try to
 *     reconnect after some time and a few times because networking problems are
 *     often temporary.
 *
 *  5) The UI React app needs to be able to show all kinds of bells and whistles
 *     to the user reflecting all sorts of state changes here. This means that
 *     we need to hook some of the state changes of this system into the Redux
 *     state from which the React app renders itself.
 */
class WebSocketManager implements IWebSocketManager {
  private static _singleton: WebSocketManager;

  /**
   * Channel facilitating orchestration of concurrent sessions. In other words,
   * e.g. browser tabs message each other using this channel to organize their
   * privileges such as the right to hold the single WebSocket connection allowed
   * for a single user at any given time.
   */
  private _session_orchestration_channel: BroadcastChannel | null = null;
  private readonly _session_orchestration_channel_id = 'stellarq_session_orchestration' as const;
  private _socket: IWebSocketAbortable | null = null;
  private readonly _connection_policy: WebSocketConnectionPolicy;
  /** Event listeners associated with the WebSocket. */
  private readonly _event_listeners_ws = new Map<keyof WebSocketEventMap, any>();
  /** Event listeners associated with the BroadcastChannel. */
  private readonly _event_listeners_bc = new Map<keyof BroadcastChannelEventMap, EventListener>();
  private readonly _ws_api_url: URL;
  private _redux: Hooks;
  private _cm: IConnectionManager;

  constructor(connection_policy: WebSocketConnectionPolicy, redux: Hooks) {
    if (WebSocketManager._singleton) {
      throw new Error('Attempted to re-instantiate singleton WebSocketManager');
    } else {
      WebSocketManager._singleton = this;
    }

    const url = new URL(window.location.toString());
    const protocol: 'ws:' | 'wss:' = url.protocol === 'http:' ? 'ws:' : 'wss:';
    const hostname = url.hostname;
    const port = protocol === 'ws:' ? 25790 : url.port; // 25790 in localenv, defaults to current port in other contexts (e.g. prod, local-staging)
    const path = '/api/sock';
    this._ws_api_url = new URL(`${protocol}${hostname}:${port}${path}`);
    this._connection_policy = connection_policy;

    this._redux = redux;
    this._cm = getCm();
  }

  public async connect(base64_encoded_jwt: string): Promise<void> {
    // Attachment window is allowed to open without invalidating the main window, or connecting websocket
    if (isAttachmentPath()) return;

    if (this._socket) {
      makeLog(
        Loggable.BugActionable,
        new Error('Cannot connect WebSocket: Already connected'),
        new ActionableErrorContext(null, 'connect WebSocket'),
      );
      return;
    }

    if (this._redux.get_state().session.status.invalidated_at_client_time) {
      return;
    }
    if (!checkJWTNotExpired()) {
      return;
    }

    this._session_orchestration_channel = new BroadcastChannel(this._session_orchestration_channel_id);
    const session_orchestration_listener = this.handle_session_orchestration_message.bind(this);
    this._session_orchestration_channel.addEventListener('message', session_orchestration_listener);
    this._event_listeners_bc.set('message', session_orchestration_listener);

    const connection_url = new URL(this._ws_api_url);
    for (const [search_param] of connection_url.searchParams.entries()) {
      connection_url.searchParams.delete(search_param);
    }
    connection_url.searchParams.append('neurojwt', base64_encoded_jwt);

    let err_ws: Error | null = null;
    for (let attempt_no = 0; attempt_no < this._connection_policy.max_connection_attempts_count; attempt_no++) {
      if (this._socket?._aborted) {
        this._socket = null;
        break;
      }
      try {
        this._socket = this._cm.connect_websocket(connection_url);
      } catch (err_ws_instantiation) {
        err_ws = <Error>err_ws_instantiation;
        break;
      }

      // wait for the socket to open till some timeout
      let temp_open_cb: (() => unknown) | null = null;
      await new Promise<void>(resolve => {
        const timeout_id = setTimeout(() => {
          if (this._socket) {
            this._socket.close(); // abort connection attempt
            return resolve();
          }
        }, this._connection_policy.connection_attempt_timeout_ms);
        temp_open_cb = () => {
          clearTimeout(timeout_id);
          resolve();
        };
        if (this._socket) this._socket.addEventListener('open', temp_open_cb);
      });
      if (temp_open_cb !== null) {
        this._socket.removeEventListener('open', temp_open_cb);
      }

      if (this._socket.readyState === 1) {
        // 1 = OPEN
        break;
      }

      // wait a bit before the next attempt
      await new Promise<void>(resolve => setTimeout(resolve, this._connection_policy.connection_reattempt_delay_ms));
    }

    if (this._socket === null || err_ws !== null || this._socket.readyState !== 1) {
      /*
       * Case e.g.
       * - Network not usable (in which case attempting to make error logs is of
       *   no use either), or
       * - Connection was aborted midway by e.g. switching between patients
       *   fast enough on a slow enough connection: Note that the WebSocket
       *   connection is assumed to be driven by entering a patient context.
       *   Switching to new patient is expected to yield a new WebSocket
       *   connection, in which case reaching this branch for the old,
       *   obsoleted, aborted connection is not an error.
       *
       *   TODO: Distinguish between the two scenarios:
       *     1) Offline -- Non-actionable error case where usage of Neuro should be rendered disabled?
       *     2) Aborted due to patient context switch -- Not an error case.
       *
       *   One possible solution would be to eliminate the need for recreating
       *   WebSocket connection when switching patients. Instead, the same
       *   connection could be used, and the patient context switch could happen
       *   on the backend side for the same socket (i.e. just start sending
       *   another patient's related events over the existing socket, and stop
       *   sending the previous context's events).
       */
      return;
    }

    const close_listener = this.handle_close.bind(this);
    this._socket.addEventListener('close', close_listener);
    this._event_listeners_ws.set('close', close_listener);

    const error_listener = this.handle_error.bind(this);
    this._socket.addEventListener('error', error_listener);
    this._event_listeners_ws.set('error', error_listener);

    const message_listener = this.handle_message.bind(this);
    this._socket.addEventListener('message', message_listener);
    this._event_listeners_ws.set('message', message_listener);
  }

  public disconnect(): void {
    this.close_websocket();
    this.close_broadcastchannel();
    return;
  }

  /** Handle a WebSocket's `close` event. */
  private handle_close(event: CloseEvent): void {
    this.close_broadcastchannel();
    this._redux.dispatch(this._redux.action_creator_websocket_closed());
    for (const [type, listener] of this._event_listeners_ws) {
      if (this._socket === null) {
        break;
      }
      this._socket.removeEventListener(type, listener);
    }
    this._socket = null;

    const jwt = parseJWTFromCookie();
    if (!jwt) {
      return;
    }
    if (!event.wasClean) {
      this.connect(jwt);
    } else {
      switch (event.code as CloseReason) {
        case CloseReason.ServerTerminating: {
          this.connect(jwt);
          break;
        }
        case CloseReason.UserSessionDuplicated: {
          this._redux.dispatch(sessionInvalidatedAction({ type: event.reason }));
          makeLog(
            Loggable.UsageAnalytic,
            new UsageAnalyticDatapoint({
              scenario: 'WebSocket session was invalidated by backend event: UserSessionDuplicated',
              code: CloseReason.UserSessionDuplicated,
            }),
          );
          break;
        }
        case CloseReason.UserSessionExpired:
        default: {
          // nothing to do here...
          break;
        }
      }
    }
  }

  /** Handle a WebSocket's `error` event. */
  private handle_error(event: Event): void {
    // TODO: when does this happen? what's the event's content like?
    makeLog(
      Loggable.BugActionable,
      new Error(`WebSocket error event: ${JSON.stringify(event)}`),
      new ActionableErrorContext(null, 'WebSocket lifecycle'),
    );
  }

  /** Handle a WebSocket's `message` event. */
  private handle_message(event: MessageEvent): void {
    let message: PubSub.IBaseMessage;
    try {
      message = JSON.parse(event.data);
    } catch (err_parse) {
      makeLog(
        Loggable.BugActionable,
        new Error('Could not parse message received from WebSocket as JSON: ' + event?.data, { cause: err_parse }),
        new ActionableErrorContext(null, 'deserialize WebSocket message'),
      );
      return;
    }

    switch (message?.type) {
      case PubSubEventTypes.HCP_SUBSCRIBED:
      case PubSubEventTypes.HCP_UNSUBSCRIBED: {
        const message_asserted = <PubSub.ISubscriptionSummaryMessage>message;
        this._redux.dispatch(this._redux.action_creator_get_subscription_message(message_asserted));
        break;
      }
      case NeuroEventTypes.ACTION_COMMIT_FINISHED:
      case NeuroEventTypes.ACTION_COMMIT_INITIATED:
      case NeuroEventTypes.ACTION_COMMIT_DISCARDED:
      case NeuroEventTypes.ACTION_COMMIT_PUT:
      case NeuroEventTypes.ACTION_DOCUMENT_CREMMITTED: {
        const message_asserted = <PubSub.IPatientDocumentMessage>message;
        if (message_asserted.patientId !== this._redux.get_state().session.data?.patientid) {
          makeLog(
            Loggable.BugActionable,
            new Error(
              `Websocket message patient id (${message_asserted.patientId}) differs from user jwt patient id (${this._redux.get_state().session.data?.patientid})`,
            ),
            new ActionableErrorContext(null, 'Verify websocket event patient id match with user selected patient id'),
          );
        }
        if (message_asserted.documentType && message_asserted.documentId) {
          void actions.updateDocumentFromServer(
            {
              name: message_asserted.documentType,
              id: message_asserted.documentId,
            },
            'websocket document update event',
          )(this._redux.dispatch);
        }
        break;
      }
      case NeuroEventTypes.ACTION_DOCUMENT_DELETED: {
        const message_asserted = <PubSub.IPatientDocumentMessage>message;
        if (message_asserted.documentId) {
          this._redux.dispatch(
            reduxActions.deleteDocumentAction(message_asserted.documentId, {
              removeDate: new Date().toUTCString(),
              remover: 'Websocket',
              removerOrg: 'Websocket',
              removeReason: 'Websocket',
            }),
          );
        }
        break;
      }
      case NeuroEventTypes.ACTION_COMMIT_UPDATED:
      case NeuroEventTypes.ACTION_DOCUMENT_CREATED: {
        // nothing to do
        break;
      }
      default: {
        makeLog(
          Loggable.BugActionable,
          new Error('Unknown type of message from WebSocket: ' + message?.type),
          new ActionableErrorContext(null, 'identify WebSocket message type'),
        );
      }
    }
  }

  /** Managed API for `BroadcastChannel#postMessage`. */
  public post_session_orchestration_message(message: any): void {
    if (this._session_orchestration_channel === null) {
      return;
    } else {
      this._session_orchestration_channel.postMessage(message);
    }
  }

  /** Handle a BroadcastChannel's `message` event. */
  private handle_session_orchestration_message(event: any): void {
    switch (event?.data?.type) {
      /* When a duplicated browsing context notifies that it has loaded the
      cookie-based session, then dispatch invalidation action in the current (old)
      browsing context. */
      case LOAD: {
        this.disconnect();
        this._redux.dispatch(sessionInvalidatedAction(event.data));
        makeLog(
          Loggable.UsageAnalytic,
          new UsageAnalyticDatapoint({
            scenario: 'WebSocket session was invalidated by local event: load session',
            code: LOAD,
          }),
        );
        break;
      }
      default: {
        makeLog(
          Loggable.BugActionable,
          new Error(`Unknown type of event for '${event?.target}': ${event?.data?.type}`),
          new ActionableErrorContext(null, 'handle WebSocket session orchestration message'),
        );
        break;
      }
    }
  }

  /** Close and free resources. */
  private close_websocket(): void {
    if (this._socket === null) {
      return;
    }
    for (const [type, listener] of this._event_listeners_ws) {
      this._socket.removeEventListener(type, listener);
    }
    this._event_listeners_ws.clear();

    /* https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState */
    switch (this._socket.readyState) {
      case 0:

      case 1: {
        try {
          this._socket.close();
          this._socket = null;
          break;
        } catch (err_ws_close) {
          makeLog(
            Loggable.BugActionable,
            /**
             * https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/close#exceptions
             */
            err_ws_close as DOMException,
            new ActionableErrorContext(null, 'close WebSocket'),
          );
          break;
        }
      }

      case 2:

      case 3: {
        makeLog(
          Loggable.BugActionable,
          new Error(
            'Attempted to close a closing or closed WebSocket: readyState: ' + this._socket.readyState.toString(),
          ),
          new ActionableErrorContext(null, 'close WebSocket'),
        );
        break;
      }

      default: {
        makeLog(
          Loggable.BugActionable,
          new Error(
            `Unknown WebSocket.readyState (typeof ${typeof this._socket.readyState}): '${this._socket.readyState}'`,
          ),
          new ActionableErrorContext(null, 'close WebSocket'),
        );
        break;
      }
    }
  }

  /** Close and free resources. */
  private close_broadcastchannel(): void {
    if (this._session_orchestration_channel === null) {
      return;
    } else {
      for (const [event_type, listener_ref] of this._event_listeners_bc) {
        this._session_orchestration_channel.removeEventListener(event_type, listener_ref);
      }
      this._session_orchestration_channel.close(); // pass to gc
      this._session_orchestration_channel = null;
    }
  }
}

export default WebSocketManager;
